import dayjs from "dayjs";
import { useState } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { MultiSelect, type Option } from "@/components/ui/multi-select";
import { type FormReadCorrectionReason, groupByEncounter, REASON_LABELS, REASONS } from "./corrections";
import { ROUTES } from "./navigation";
import { useFormReadCorrectionList } from "./useFormReadCorrections";

const reasonOptions: Option[] = REASONS.map((reason) => ({ value: reason, label: REASON_LABELS[reason] }));

export function FormReadCorrectionsPage() {
  const [includeAnswered, setIncludeAnswered] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState<Option[]>([]);
  const [lowConfidenceOnly, setLowConfidenceOnly] = useState(false);
  const { rows, total, isLoading, error, hasNextPage, fetchNextPage, isFetchingNextPage } = useFormReadCorrectionList({
    includeAnswered,
    reasons: selectedReasons.map((option) => option.value as FormReadCorrectionReason),
    lowConfidenceOnly,
  });

  const encounters = groupByEncounter(rows);

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b pb-3">
        <div className="flex items-baseline gap-2">
          <h1 className="text-base font-semibold">Form-read corrections</h1>
          <span className="text-sm text-muted-foreground">
            LNW pre-op form rows the judge flagged, for admin review.
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div className="w-full sm:w-64 sm:shrink-0">
            <MultiSelect
              options={reasonOptions}
              value={selectedReasons}
              onChange={setSelectedReasons}
              placeholder="All reasons"
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="low-confidence-only"
              checked={lowConfidenceOnly}
              onCheckedChange={(checked) => setLowConfidenceOnly(checked === true)}
            />
            <Label htmlFor="low-confidence-only">Low confidence only</Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="include-answered"
              checked={includeAnswered}
              onCheckedChange={(checked) => setIncludeAnswered(checked === true)}
            />
            <Label htmlFor="include-answered">Include answered rows</Label>
          </div>
        </div>
      </div>

      {isLoading && <div className="text-sm text-muted-foreground">Loading corrections…</div>}
      {error && <div className="text-destructive">{error.message}</div>}
      {!isLoading && !error && encounters.length === 0 && (
        <div className="text-sm text-muted-foreground">No form-read corrections to review.</div>
      )}

      {encounters.length > 0 && (
        <ul className="divide-y rounded-md border">
          {encounters.map(({ encounterId, judgedAt, reasonCounts, lowConfidenceCount }) => (
            <li key={encounterId}>
              <Link
                to={`${ROUTES.formReadCorrections.route}/${encounterId}`}
                className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-muted"
              >
                <span className="font-mono text-sm">{encounterId}</span>
                <span className="text-sm text-muted-foreground">
                  {REASONS.filter((reason) => reasonCounts[reason] > 0)
                    .map((reason) => `${reasonCounts[reason]} ${REASON_LABELS[reason].toLowerCase()}`)
                    .join(" · ")}
                  {lowConfidenceCount > 0 && ` · ${lowConfidenceCount} low confidence`}
                  {` · judged ${dayjs(judgedAt).format("D MMM YYYY")}`}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {hasNextPage && (
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span>
            Showing the newest {rows.length} of {total} rows. Counts for the oldest encounter may be incomplete.
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            Show more
          </Button>
        </div>
      )}
    </div>
  );
}
